import { Box, Button, Stack, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { ListItemBox } from "lib/@components/ListItemBox"
import Iconify from "minimals-template/components/Iconify"
import { useDialog } from "lib/@hooks/useDialog"
import { confirm } from "lib/@dialogs/confirm"
import { Sortable } from "lib/@components/Sortable"
import { Repeat } from "lib/@components/repeat"
import { generate } from "library/guid/index.mjs"
import { RateCardModifierRow } from "./rate-card-modifier-row"
import { RateCardModifierDialog } from "./rate-card-modifier-dialog"
import { isLegacyScopedModifier } from "./rate-card-modifier-scope"

export function RateCardModifiersSection({ modifiers, tradeLines, currency, onUpdate, readOnly }) {
    const openModifierDialog = useDialog(<RateCardModifierDialog />, { maxWidth: "sm", fullWidth: true })
    const tradeOptions = (tradeLines || []).map(toTradeOption)
    const showScope = (modifiers || []).some(isLegacyScopedModifier)

    return (
        <Box sx={{ mt: 4 }} data-cy="rate-card-modifiers-section">
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1">Modifiers</Typography>
                <Typography variant="body2" color="text.secondary">
                    Ordered adjustments layered on top of the trade rates, applied to the whole regime. Examples:
                    management fee (+10%), profit margin. For scoped adjustments (e.g. by trade or facility), use a rule
                    in a rule set.
                </Typography>
            </Stack>
            <If value={!modifiers?.length}>
                <Box
                    sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 3,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        No modifiers yet. Add one to apply a percentage or fixed adjustment to the whole regime.
                    </Typography>
                    <If value={!readOnly}>
                        <Button
                            data-cy="add-modifier"
                            variant="contained"
                            size="small"
                            startIcon={<Iconify icon="mdi:plus" />}
                            onClick={handleAdd}
                        >
                            Add modifier
                        </Button>
                    </If>
                </Box>
            </If>
            <If value={!!modifiers?.length}>
                <ListItemBox sx={{ bgcolor: "background.neutral", borderRadius: 1, px: 2, py: 1.5, gap: 2 }}>
                    <Box sx={{ width: 32, flexShrink: 0 }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 16 }}>
                            Name
                        </Typography>
                    </Box>
                    <If value={showScope}>
                        <Box sx={{ width: 220 }} data-cy="modifier-scope-column">
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 16 }}>
                                Scope (legacy)
                            </Typography>
                        </Box>
                    </If>
                    <Box sx={{ width: 140, textAlign: "right" }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 16 }}>
                            Value
                        </Typography>
                    </Box>
                    <Box sx={{ width: 160 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: 16 }}>
                            Calculation basis
                        </Typography>
                    </Box>
                    <Box sx={{ width: 80, flexShrink: 0 }} />
                </ListItemBox>
                <If value={!readOnly}>
                    <Sortable items={modifiers} id="id" onOrdered={handleReorder}>
                        <Repeat
                            list={modifiers}
                            item={
                                <RateCardModifierRow
                                    tradeOptions={tradeOptions}
                                    modifiers={modifiers}
                                    currency={currency}
                                    showScope={showScope}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            }
                            keyFn={getModifierKey}
                        />
                    </Sortable>
                    <Box sx={{ mt: 1.5 }}>
                        <Button
                            data-cy="add-modifier"
                            variant="outlined"
                            size="small"
                            startIcon={<Iconify icon="mdi:plus" />}
                            onClick={handleAdd}
                        >
                            Add modifier
                        </Button>
                    </Box>
                </If>
                <If value={readOnly}>
                    <Repeat
                        list={modifiers}
                        item={
                            <RateCardModifierRow
                                tradeOptions={tradeOptions}
                                modifiers={modifiers}
                                currency={currency}
                                showScope={showScope}
                                readOnly
                            />
                        }
                        keyFn={getModifierKey}
                    />
                </If>
            </If>
        </Box>
    )

    async function handleAdd() {
        if (readOnly) return
        const result = await openModifierDialog({ tradeOptions })
        if (!result) return
        const next = [...(modifiers || []), { ...result, id: generate() }]
        onUpdate(next)
    }

    async function handleEdit(modifier) {
        if (readOnly) return
        const result = await openModifierDialog({ modifier, tradeOptions })
        if (!result) return
        const next = (modifiers || []).map(replaceEditedModifier)
        onUpdate(next)

        function replaceEditedModifier(m) {
            if (m.id === modifier.id) return { ...modifier, ...result, id: modifier.id }
            return m
        }
    }

    async function handleDelete(modifier) {
        if (readOnly) return
        const ok = await confirm(`Delete modifier "${modifier.name}"?`, "Delete modifier")
        if (!ok) return
        const next = (modifiers || []).filter(isNotDeletedModifier)
        onUpdate(next)

        function isNotDeletedModifier(m) {
            return m.id !== modifier.id
        }
    }

    function handleReorder(reordered) {
        if (readOnly) return
        // Sortable reorders the array in place and calls onOrdered with the mutated array.
        // Copy so React sees a new reference and persists through the save chain.
        onUpdate([...reordered])
    }

    function getModifierKey(modifier) {
        return modifier.id
    }

    function toTradeOption(l) {
        return { id: l.tradeId, label: l.tradeName || l.tradeId }
    }
}
